import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Info, CheckCircle2, ShieldAlert } from "lucide-react";
import {
  computeCascadePreview,
  computeWarningLevel,
  friendlyPathLabel,
  parseBasePath,
  denormLang,
} from "./cascadePreview";

/**
 * BaseEditDialog — QUESTIONNAIRE-2.0-A-S2-γ-A (stand-alone).
 *
 * Modal for editing a single base-language (EN/ES/ZH) i18n field value
 * BEFORE submitting a PATCH /i18n with `value`. Surfaces:
 *   - the current stored value (read-only reference)
 *   - an editable proposed value
 *   - a live cascade preview (count + affected languages list)
 *   - graduated warning banner (ok/info/warn/danger)
 *   - submit button that is locked unless the proposed value is non-empty
 *     and different from the current value
 *
 * NOT yet wired into the Builder. γ-B will mount it from QuestionnaireBuilder
 * and pass `onConfirm({ path, value }) => Promise<void>` that calls
 * `useI18nPatch.patchField`.
 *
 * Props:
 *   open                 Boolean — controls visibility (uses shadcn Dialog).
 *   path                 ts_path of the field, e.g. "title_en". Required.
 *   currentValue         String — the stored value as read from the doc.
 *   summary              Summary payload from /translation-summary (for langs).
 *   translation_status   Flat status map from the doc (for cascade preview).
 *   busy                 Boolean — true while parent's PATCH is in flight.
 *   error                String|null — displayed inline on failure.
 *   onCancel             () => void — close handler.
 *   onConfirm            ({ path, value }) => Promise<void> | void — submit.
 *
 * Out of scope γ-A:
 *   - Fetching anything. The parent passes summary + translation_status.
 *   - Calling PATCH directly. The parent's `onConfirm` is responsible.
 *   - 409 reload UI (lives in γ-D or the parent).
 */

const TONE_CLASSES = {
  emerald: { wrap: "bg-emerald-50 border-emerald-300 text-emerald-900", icon: "text-emerald-600" },
  sky:     { wrap: "bg-sky-50 border-sky-300 text-sky-900",             icon: "text-sky-600" },
  amber:   { wrap: "bg-amber-50 border-amber-300 text-amber-900",       icon: "text-amber-700" },
  rose:    { wrap: "bg-rose-50 border-rose-300 text-rose-900",          icon: "text-rose-700" },
};

const ICONS_BY_TONE = {
  emerald: CheckCircle2,
  sky:     Info,
  amber:   AlertTriangle,
  rose:    ShieldAlert,
};

const BaseEditDialog = ({
  open,
  path,
  currentValue = "",
  summary = null,
  translation_status = {},
  busy = false,
  error = null,
  onCancel = () => {},
  onConfirm = () => {},
}) => {
  // The parent (QuestionnaireBuilder) controls lifecycle via `key={path}` so a
  // path change remounts the dialog and we get a fresh `proposed` state from
  // `currentValue` without needing a reset useEffect.
  const [proposed, setProposed] = useState(currentValue || "");
  // QUESTIONNAIRE-2.0-A-S2-γ-D-D — Inline confirm-on-cancel guard for dirty
  // state. When the admin types a non-empty value distinct from currentValue
  // and tries to close/cancel, we surface a guard so they don't lose the text.
  const [confirmCancel, setConfirmCancel] = useState(false);

  const parsed = useMemo(() => parseBasePath(path), [path]);
  const langDisplay = parsed ? denormLang(parsed.baseLangNorm).toUpperCase() : "";
  const friendly = useMemo(() => friendlyPathLabel(path), [path]);

  const cascade = useMemo(
    () => computeCascadePreview({ translation_status, basePath: path, summary }),
    [translation_status, path, summary],
  );

  const warning = useMemo(() => computeWarningLevel(cascade.count), [cascade.count]);
  const toneCls = TONE_CLASSES[warning.tone] || TONE_CLASSES.sky;
  const ToneIcon = ICONS_BY_TONE[warning.tone] || Info;

  const trimmedProposed = (proposed || "").trim();
  const trimmedCurrent  = (currentValue || "").trim();
  const isUnchanged     = trimmedProposed === trimmedCurrent;
  const isEmpty         = trimmedProposed.length === 0;
  const isLocked        = busy || isEmpty || isUnchanged;
  // QUESTIONNAIRE-2.0-A-S2-γ-D-D — Dirty = admin typed a non-empty value that
  // differs from what they loaded. Used to gate the cancel/close action.
  const isDirty = !isEmpty && !isUnchanged;

  const handleConfirm = async () => {
    if (isLocked) return;
    await onConfirm({ path, value: proposed });
  };

  // QUESTIONNAIRE-2.0-A-S2-γ-D-D — Cancel handler with dirty guard.
  // First click while dirty shows the inline confirm; second click (or Yes)
  // proceeds to call the parent's onCancel.
  const handleCancelRequest = () => {
    if (busy) return;
    if (isDirty && !confirmCancel) {
      setConfirmCancel(true);
      return;
    }
    setConfirmCancel(false);
    onCancel();
  };

  // Cap the affected lang list to avoid runaway DOM when many langs reviewed.
  const langsPreview = cascade.langs.slice(0, 12);
  const langsExtra   = Math.max(0, cascade.langs.length - langsPreview.length);

  return (
    <Dialog
      open={!!open}
      onOpenChange={(o) => { if (!o && !busy) handleCancelRequest(); }}
    >
      <DialogContent
        className="sm:max-w-lg"
        data-testid="base-edit-dialog"
        data-path={path || ""}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ToneIcon className={`w-5 h-5 ${toneCls.icon}`} />
            Edit base text · <span className="font-mono text-sm">{langDisplay}</span>
          </DialogTitle>
          <DialogDescription className="pt-1 text-slate-600">
            <span data-testid="base-edit-dialog-friendly-path" className="font-mono text-xs text-slate-500">
              {friendly}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Current value */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Current value (read-only)
            </label>
            <pre
              data-testid="base-edit-dialog-current-value"
              className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-700"
            >
              {currentValue || <em className="text-slate-400">(empty)</em>}
            </pre>
          </div>

          {/* Proposed value */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Proposed value
            </label>
            <textarea
              data-testid="base-edit-dialog-input"
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              rows={4}
              value={proposed}
              onChange={(e) => setProposed(e.target.value)}
              disabled={busy}
            />
            <div className="mt-1 flex justify-between text-[10px] text-slate-500">
              <span>
                {isUnchanged && !isEmpty && (
                  <span data-testid="base-edit-dialog-unchanged-hint">Unchanged — modify the value to enable save.</span>
                )}
                {isEmpty && (
                  <span data-testid="base-edit-dialog-empty-hint" className="text-rose-700">Value cannot be empty.</span>
                )}
              </span>
              <span data-testid="base-edit-dialog-length">{proposed.length} chars</span>
            </div>
          </div>

          {/* Cascade preview banner */}
          <div
            data-testid="base-edit-dialog-cascade-preview"
            data-cascade-level={warning.level}
            data-cascade-count={cascade.count}
            className={`rounded-md border px-3 py-2 text-xs ${toneCls.wrap}`}
          >
            <div className="flex items-center gap-2 font-semibold">
              <span aria-hidden>{warning.icon}</span>
              <span data-testid="base-edit-dialog-cascade-message">{warning.message}</span>
            </div>
            {cascade.count > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1" data-testid="base-edit-dialog-cascade-langs">
                {langsPreview.map((L) => (
                  <span
                    key={L.language_code}
                    data-testid={`base-edit-dialog-cascade-lang-${L.language_code}`}
                    className={`inline-flex items-center gap-1 rounded border bg-white/70 px-1.5 py-0.5 font-mono text-[10px] uppercase
                      ${L.direction === "rtl" ? "border-purple-300 text-purple-900" : "border-slate-300 text-slate-700"}`}
                    title={`${L.language_name_native || L.language_code}${L.direction === "rtl" ? " · RTL" : ""}`}
                  >
                    {L.language_code}
                    {L.direction === "rtl" && <span className="text-[8px]">RTL</span>}
                  </span>
                ))}
                {langsExtra > 0 && (
                  <span
                    data-testid="base-edit-dialog-cascade-extra"
                    className="inline-flex items-center rounded border border-slate-300 bg-white/70 px-1.5 py-0.5 font-mono text-[10px] text-slate-600"
                  >
                    +{langsExtra} more
                  </span>
                )}
              </div>
            )}
            {!cascade.applicable && cascade.reason === "not_base_path" && (
              <div data-testid="base-edit-dialog-not-base-warning" className="mt-1 text-[10px] text-slate-600">
                This path is not a base-lang field — cascade preview not applicable.
              </div>
            )}
            {cascade.applicable && cascade.reason === "no_summary" && (
              <div data-testid="base-edit-dialog-no-summary-warning" className="mt-1 text-[10px] text-slate-600">
                Translation summary not available — preview disabled. The PATCH will still
                trigger server-side cascade if reviewed translations exist.
              </div>
            )}
          </div>

          {/* Cross-base lang note (per γ analysis §17) */}
          {cascade.applicable && (
            <p className="text-[10px] text-slate-500">
              Note: editing any base language ({langDisplay}) invalidates reviewed translations in
              every non-base language. This is the intended behavior — translations should be re-reviewed
              after the source text changes.
            </p>
          )}

          {/* Inline error from parent (e.g., 409, 422) */}
          {error && (
            <div
              data-testid="base-edit-dialog-error"
              className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800"
            >
              {error}
            </div>
          )}
          {/* QUESTIONNAIRE-2.0-A-S2-γ-D-D — Confirm-on-cancel inline guard.
              Shown when the admin clicks Cancel/X while there is unsaved dirty
              text. Two explicit choices: "Yes, discard" or "Keep editing". */}
          {confirmCancel && (
            <div
              data-testid="base-edit-dialog-confirm-cancel-guard"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              You have unsaved changes ({proposed?.length || 0} chars). Discard them?
              <button
                type="button"
                data-testid="base-edit-dialog-confirm-cancel-yes"
                onClick={() => { setConfirmCancel(false); onCancel(); }}
                className="ml-2 underline font-semibold hover:text-amber-700"
              >
                Yes, discard
              </button>
              {" · "}
              <button
                type="button"
                data-testid="base-edit-dialog-confirm-cancel-keep"
                onClick={() => setConfirmCancel(false)}
                className="underline hover:text-amber-700"
              >
                Keep editing
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={handleCancelRequest}
            disabled={busy}
            data-testid="base-edit-dialog-cancel"
            className="tsw-btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLocked}
            data-testid="base-edit-dialog-confirm"
            data-locked={isLocked ? "true" : "false"}
            className={`tsw-btn-primary ${warning.level === "danger" ? "!bg-rose-600 hover:!bg-rose-700" : warning.level === "warn" ? "!bg-amber-600 hover:!bg-amber-700" : ""}`}
          >
            {busy ? "Saving…" : "Save base text & trigger cascade"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BaseEditDialog;
